import { num, type ComponentForm } from '../component-types.js';
import { POST_PROFILE_FIELDS } from './post-process.js';

/**
 * `liquid` (water, lava, acid, quicksand…): a box volume whose top face is
 * the surface. Physics, swimming, damage, waves, the surface material (the
 * entity's Material, with any animated texture) and the underwater look are
 * all set here (see engine/docs/systems/liquids.md).
 */
const form: ComponentForm = {
  key: 'liquid',
  label: 'Liquid',
  icon: 'water',
  order: 34,
  defaults: {
    kind: 'water',
    size: [10, 2, 10],
    waves: [{ amplitude: 0.05, wavelength: 3, speed: 0.8, direction: [1, 0.3] }],
    underwater: {
      fog: { color: [0.1, 0.35, 0.45], density: 0.15 },
      colorAdjustments: { colorFilter: [0.7, 0.9, 1], saturation: -15 },
      distortion: { strength: 0.006 },
    },
  },
  fields: [
    { key: 'kind', label: 'Kind', type: { kind: 'string', placeholder: 'water, lava, acid…' }, hint: 'A label scripts read with liquid_kind().' },
    { key: 'size', label: 'Volume size', type: { kind: 'vec3' }, hint: 'Meters (× scale). The entity is the center of the surface; the volume goes y meters down.' },
    { key: 'density', label: 'Density (kg/m³)', type: num(50, { min: 0 }), read: (v) => v.density ?? 1000, hint: 'Water 1000, lava ~3100. Lighter bodies float.' },
    { key: 'buoyancy', label: 'Buoyancy', type: num(0.1, { min: 0 }), read: (v) => v.buoyancy ?? 1, hint: '1 = physical, 0 = everything sinks.' },
    { key: 'linearDrag', label: 'Drag', type: num(0.1, { min: 0 }), read: (v) => v.linearDrag ?? 1.5 },
    { key: 'angularDrag', label: 'Spin drag', type: num(0.1, { min: 0 }), read: (v) => v.angularDrag ?? 1 },
    { key: 'flow', label: 'Current (m/s)', type: { kind: 'vec3' }, hint: 'Rivers, conveyor lava, whirlpools (set from a script).' },
    { key: 'flowStrength', label: 'Current strength', type: num(0.1, { min: 0 }), read: (v) => v.flowStrength ?? 1 },
    { key: 'gravityScale', label: 'Gravity inside', type: num(0.1), read: (v) => v.gravityScale ?? 1, hint: '< 1 floaty, 0 zero-g.' },
    {
      key: 'swimming',
      label: 'Swimming (characters)',
      type: {
        kind: 'object',
        fields: [
          { key: 'enabled', label: 'Can swim', type: { kind: 'bool' }, read: (v) => v.enabled ?? true, hint: 'Off: characters wade / sink (lava, quicksand).' },
          { key: 'speed', label: 'Speed ×', type: num(0.05, { min: 0 }), read: (v) => v.speed ?? 0.6 },
          { key: 'buoyancy', label: 'Buoyancy', type: num(0.1, { min: 0 }), read: (v) => v.buoyancy ?? 1 },
          { key: 'floatDepth', label: 'Float depth (m)', type: num(0.05), read: (v) => v.floatDepth ?? 0.6 },
          { key: 'drag', label: 'Drag', type: num(0.1, { min: 0 }), read: (v) => v.drag ?? 3 },
          { key: 'stroke', label: 'Swim-up speed', type: num(0.1, { min: 0 }), read: (v) => v.stroke ?? 3, hint: 'Jump while swimming.' },
        ],
      },
    },
    { key: 'damagePerSecond', label: 'Damage / s', type: num(1), hint: 'Sent to scripts as on_liquid_damage(liquid, amount).' },
    { key: 'damageInterval', label: 'Damage interval (s)', type: num(0.05, { min: 0.01 }), read: (v) => v.damageInterval ?? 0.5 },
    { key: 'affectsTags', label: 'Only affects tags', type: { kind: 'tags' } },
    {
      key: 'surface',
      label: 'Surface',
      type: {
        kind: 'object',
        fields: [
          { key: 'visible', label: 'Visible', type: { kind: 'bool' }, read: (v) => v.visible ?? true },
          { key: 'resolution', label: 'Resolution', type: num(4, { min: 1, max: 512, integer: true }), read: (v) => v.resolution ?? 48 },
          { key: 'tileSize', label: 'Texture tile (m)', type: num(0.25, { min: 0 }), read: (v) => v.tileSize ?? 2 },
          { key: 'doubleSided', label: 'Visible from below', type: { kind: 'bool' }, read: (v) => v.doubleSided ?? true },
          { key: 'fillBody', label: '2D: fill body', type: { kind: 'bool' }, read: (v) => v.fillBody ?? true },
        ],
      },
      hint: 'Look: add a Material to this entity (any shader, animated texture with textureAnimation).',
    },
    {
      key: 'waves',
      label: 'Waves',
      type: { kind: 'json' },
      hint: '[{ "amplitude": 0.1, "wavelength": 4, "speed": 1, "direction": [1, 0], "steepness": 0 }, …] — summed.',
    },
    {
      key: 'underwater',
      label: 'Underwater (camera)',
      type: {
        kind: 'object',
        fields: [
          { key: 'priority', label: 'Priority', type: num(1), read: (v) => v.priority ?? 100 },
          { key: 'transition', label: 'Fade-in depth (m)', type: num(0.05, { min: 0 }), read: (v) => v.transition ?? 0.15 },
          {
            key: 'fog',
            label: 'Fog',
            type: {
              kind: 'object',
              fields: [
                { key: 'color', label: 'Color', type: { kind: 'color', alpha: false } },
                { key: 'density', label: 'Density', type: num(0.01, { min: 0 }), hint: '0.05 clear … 1 murky … 3 lava.' },
              ],
            },
          },
          ...POST_PROFILE_FIELDS,
        ],
      },
    },
  ],
};
export default form;
